// frontend/src/pages/Cofre.js — v26b
import React, { useState, useEffect, useRef, useCallback } from 'react';
import api from '../api';

// ─── Toast ────────────────────────────────────────────────────────────────────
function useToast() {
  const [msgs, setMsgs] = useState([]);
  const add = (texto, tipo = 'ok') => {
    const id = Date.now();
    setMsgs(p => [...p, { id, texto, tipo }]);
    setTimeout(() => setMsgs(p => p.filter(m => m.id !== id)), 4000);
  };
  const remove = id => setMsgs(p => p.filter(m => m.id !== id));
  return { msgs, toast: add, remove };
}
function Toast({ msgs, onRemove }) {
  return (
    <div style={{ position: 'fixed', bottom: 24, right: 24, zIndex: 9999, display: 'flex', flexDirection: 'column', gap: 8 }}>
      {msgs.map(m => (
        <div key={m.id} onClick={() => onRemove(m.id)} style={{
          background: m.tipo === 'erro' ? '#c0392b' : '#1A2233', color: '#fff',
          padding: '12px 20px', borderRadius: 8, cursor: 'pointer', fontSize: 14, maxWidth: 360,
          boxShadow: '0 4px 16px rgba(0,0,0,0.18)',
          borderLeft: `4px solid ${m.tipo === 'erro' ? '#ff6b6b' : '#D85A30'}`
        }}>{m.texto}</div>
      ))}
    </div>
  );
}

// ─── MFA Modal ───────────────────────────────────────────────────────────────
function MfaModal({ onAutenticado, onCancelar }) {
  const [etapa, setEtapa] = useState('intro');
  const [codigo, setCodigo] = useState('');
  const [loading, setLoading] = useState(false);
  const [erro, setErro] = useState('');
  const [emailMascarado, setEmailMascarado] = useState('');

  async function solicitar() {
    setLoading(true); setErro('');
    try {
      const { data } = await api.post('/cofre/mfa/solicitar');
      setEmailMascarado(data.email_mascarado);
      setEtapa('codigo');
    } catch (e) { setErro(e.response?.data?.erro || 'Erro ao enviar código.'); }
    setLoading(false);
  }

  async function verificar() {
    if (codigo.length !== 6) { setErro('Digite os 6 dígitos.'); return; }
    setLoading(true); setErro('');
    try {
      await api.post('/cofre/mfa/verificar', { codigo });
      onAutenticado();
    } catch (e) { setErro(e.response?.data?.erro || 'Código inválido.'); }
    setLoading(false);
  }

  return (
    <div style={overlayStyle} onClick={e => e.target === e.currentTarget && onCancelar?.()}>
      <div style={{ ...modalStyle, maxWidth: 420, textAlign: 'center', position: 'relative' }}>
        {/* Botão X para fechar */}
        <button
          onClick={onCancelar}
          title="Voltar ao sistema"
          style={{
            position: 'absolute', top: 12, right: 14,
            background: 'none', border: 'none', fontSize: 22,
            color: '#94a3b8', cursor: 'pointer', lineHeight: 1,
            padding: '2px 6px', borderRadius: 6,
          }}
          onMouseEnter={e => e.currentTarget.style.color = '#1A2233'}
          onMouseLeave={e => e.currentTarget.style.color = '#94a3b8'}
        >×</button>

        <div style={{ fontSize: 48, marginBottom: 16 }}>🔐</div>
        <h2 style={{ color: '#1A2233', marginBottom: 8 }}>Acesso ao Cofre de Senhas</h2>
        {etapa === 'intro' && (
          <>
            <p style={{ color: '#555', marginBottom: 24, lineHeight: 1.6 }}>Por segurança, o acesso ao Cofre requer verificação por email (MFA) a cada dia.</p>
            {erro && <p style={erroStyle}>{erro}</p>}
            <button onClick={solicitar} disabled={loading} style={btnPrimary}>
              {loading ? 'Enviando...' : '📧 Enviar código por email'}
            </button>
            <button onClick={onCancelar} style={{ ...btnSecondary, marginTop: 10, display: 'block', width: '100%' }}>
              Cancelar
            </button>
          </>
        )}
        {etapa === 'codigo' && (
          <>
            <p style={{ color: '#555', marginBottom: 8 }}>Código enviado para <strong>{emailMascarado}</strong></p>
            <p style={{ color: '#888', fontSize: 13, marginBottom: 20 }}>Válido por 10 minutos.</p>
            <input value={codigo} onChange={e => setCodigo(e.target.value.replace(/\D/g, '').slice(0, 6))}
              placeholder="000000" maxLength={6} onKeyDown={e => e.key === 'Enter' && verificar()}
              style={{ fontSize: 32, letterSpacing: 12, textAlign: 'center', padding: '12px 24px',
                border: '2px solid #d4dde8', borderRadius: 8, width: '100%', boxSizing: 'border-box',
                marginBottom: 16, fontWeight: 700 }} />
            {erro && <p style={erroStyle}>{erro}</p>}
            <button onClick={verificar} disabled={loading || codigo.length !== 6} style={btnPrimary}>
              {loading ? 'Verificando...' : '✅ Confirmar acesso'}
            </button>
            <button onClick={() => { setEtapa('intro'); setCodigo(''); setErro(''); }}
              style={{ ...btnSecondary, marginTop: 8, display: 'block', width: '100%' }}>Reenviar código</button>
            <button onClick={onCancelar} style={{ ...btnSecondary, marginTop: 8, display: 'block', width: '100%' }}>
              Cancelar
            </button>
          </>
        )}
      </div>
    </div>
  );
}

// ─── Busca de cliente com lupa ────────────────────────────────────────────────
function BuscaCliente({ value, onChange }) {
  const [query, setQuery] = useState(value || '');
  const [resultados, setResultados] = useState([]);
  const [aberto, setAberto] = useState(false);
  const [loading, setLoading] = useState(false);
  const ref = useRef();
  const timerRef = useRef();

  // Atualiza texto quando valor externo muda (ao abrir modal de edição)
  useEffect(() => { setQuery(value || ''); }, [value]);

  useEffect(() => {
    function fechar(e) { if (ref.current && !ref.current.contains(e.target)) setAberto(false); }
    document.addEventListener('mousedown', fechar);
    return () => document.removeEventListener('mousedown', fechar);
  }, []);

  async function executarBusca(q) {
    setLoading(true);
    try {
      const { data } = await api.get(`/cofre/clientes-busca?q=${encodeURIComponent(q)}`);
      setResultados(data);
      setAberto(data.length > 0);
    } catch {}
    setLoading(false);
  }

  function buscar(q) {
    setQuery(q);
    onChange(q); // atualiza o form imediatamente — permite digitar livremente
    clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => executarBusca(q), 300);
  }

  // Ao focar, carrega todos se vazio ou busca pelo valor atual
  function aoFocar() {
    executarBusca(query);
  }

  function selecionar(nome) {
    setQuery(nome);
    onChange(nome);
    setAberto(false);
  }

  return (
    <div ref={ref} style={{ position: 'relative' }}>
      <div style={{ position: 'relative' }}>
        <input value={query} onChange={e => buscar(e.target.value)}
          onFocus={aoFocar}
          placeholder="🔍 Buscar cliente..." style={{ ...inputStyle, paddingRight: 36 }} />
        {loading && <span style={{ position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)', color: '#888', fontSize: 13 }}>...</span>}
      </div>
      {aberto && resultados.length > 0 && (
        <div style={{
          position: 'absolute', top: '100%', left: 0, right: 0, background: '#fff',
          border: '1px solid #d4dde8', borderRadius: 8, boxShadow: '0 4px 16px rgba(0,0,0,0.1)',
          zIndex: 100, maxHeight: 200, overflowY: 'auto'
        }}>
          {resultados.map(c => (
            <div key={c.id} onClick={() => selecionar(c.nome_exibir || c.nome)}
              style={{ padding: '10px 14px', cursor: 'pointer', fontSize: 14, borderBottom: '1px solid #f0f0f0' }}
              onMouseEnter={e => e.currentTarget.style.background = '#f5f7fa'}
              onMouseLeave={e => e.currentTarget.style.background = ''}>
              {c.nome_exibir || c.nome}
            </div>
          ))}
        </div>
      )}
      {aberto && resultados.length === 0 && query.length > 1 && !loading && (
        <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, background: '#fff',
          border: '1px solid #d4dde8', borderRadius: 8, padding: '10px 14px', fontSize: 13,
          color: '#888', zIndex: 100 }}>
          Nenhum cliente encontrado. Você pode digitar livremente.
        </div>
      )}
    </div>
  );
}

// ─── Modal Nova/Editar Senha ──────────────────────────────────────────────────
function ModalSenha({ senha, mesas, onSalvar, onFechar }) {
  const [form, setForm] = useState({
    cliente_nome:             senha?.cliente_nome || '',
    descricao:                senha?.descricao || '',
    usuario_email:            senha?.usuario_email || '',
    senha:                    '',
    link:                     senha?.link || '',
    observacao:               senha?.observacao || '',
    visibilidade:             senha?.visibilidade || 'todos',
    mesa_ids:                 senha?.mesa_ids || [],
    expira_em_dias:           senha?.expira_em_dias ?? '',
    notificacao_acao:         senha?.notificacao_acao || 'nenhuma',
    notificacao_antecedencia: senha?.notificacao_antecedencia ?? '',
    notificacao_tipo:         senha?.expira_em_dias ? 'expira' : 'nunca',
  });
  const [mostrarSenha, setMostrarSenha] = useState(false);
  const [forca, setForca] = useState(0);
  const [geradorOpts, setGeradorOpts] = useState({ maiuscula: true, minuscula: true, numeros: true, simbolos: false, tamanho: 12 });
  const [loading, setLoading] = useState(false);
  const [carregandoSenha, setCarregandoSenha] = useState(false);
  const [erro, setErro] = useState('');

  // Ao abrir edição, busca a senha descriptografada automaticamente
  useEffect(() => {
    if (senha?.id) {
      setCarregandoSenha(true);
      api.get(`/cofre/senhas/${senha.id}/revelar`)
        .then(({ data }) => {
          setForm(p => ({ ...p, senha: data.senha }));
          setMostrarSenha(false); // começa mascarada mas já preenchida
        })
        .catch(() => {}) // silencia erro — usuário pode digitar manualmente
        .finally(() => setCarregandoSenha(false));
    }
  }, [senha?.id]);

  useEffect(() => { calcularForca(form.senha); }, [form.senha]);

  function calcularForca(s) {
    let f = 0;
    if (s.length >= 8) f++;
    if (s.length >= 12) f++;
    if (/[A-Z]/.test(s)) f++;
    if (/[0-9]/.test(s)) f++;
    if (/[^A-Za-z0-9]/.test(s)) f++;
    setForca(f);
  }

  function gerarSenha() {
    let chars = '';
    if (geradorOpts.maiuscula) chars += 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
    if (geradorOpts.minuscula) chars += 'abcdefghijklmnopqrstuvwxyz';
    if (geradorOpts.numeros) chars += '0123456789';
    if (geradorOpts.simbolos) chars += '!@#$%^&*()_+-=[]{}|;:,.<>?';
    if (!chars) chars = 'abcdefghijklmnopqrstuvwxyz';
    let s = '';
    for (let i = 0; i < geradorOpts.tamanho; i++) s += chars[Math.floor(Math.random() * chars.length)];
    setForm(p => ({ ...p, senha: s }));
    setMostrarSenha(true);
  }

  async function salvar() {
    if (!form.descricao) { setErro('Descrição obrigatória.'); return; }
    if (!senha && !form.senha) { setErro('Senha obrigatória.'); return; }
    if (form.notificacao_tipo === 'expira' && !form.expira_em_dias) { setErro('Informe em quantos dias expira.'); return; }
    setLoading(true); setErro('');
    try {
      await onSalvar({
        ...form,
        expira_em_dias: form.notificacao_tipo === 'expira' ? parseInt(form.expira_em_dias) : null,
        notificacao_antecedencia: form.notificacao_acao !== 'nenhuma' ? parseInt(form.notificacao_antecedencia) || null : null,
      });
    } catch (e) { setErro(e.response?.data?.erro || 'Erro ao salvar.'); }
    setLoading(false);
  }

  const forcaLabel = ['', 'Fraca', 'Razoável', 'Boa', 'Ótima', 'Excelente'][forca] || '';
  const forcaCor  = ['', '#e74c3c', '#e67e22', '#f1c40f', '#27ae60', '#16a085'][forca] || '#ccc';

  return (
    <div style={overlayStyle}>
      <div style={{ ...modalStyle, maxWidth: 680, maxHeight: '92vh', overflowY: 'auto' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 22 }}>
          <h2 style={{ color: '#1A2233', margin: 0 }}>{senha ? 'Editar senha' : 'Nova senha'}</h2>
          <button onClick={onFechar} style={btnFechar}>×</button>
        </div>

        {erro && <p style={erroStyle}>{erro}</p>}

        {/* Cliente */}
        <div style={{ marginBottom: 16 }}>
          <label style={labelStyle}>Cliente</label>
          <BuscaCliente value={form.cliente_nome} onChange={v => setForm(p => ({ ...p, cliente_nome: v }))} />
        </div>

        {/* Descrição */}
        <div style={{ marginBottom: 16 }}>
          <label style={labelStyle}>Descrição *</label>
          <input value={form.descricao} onChange={e => setForm(p => ({ ...p, descricao: e.target.value }))}
            style={{ ...inputStyle, borderColor: !form.descricao ? '#e74c3c' : '#d4dde8' }}
            placeholder="Ex: E-mail do suporte, Kaspersky, Google Drive..." />
        </div>

        <div style={{ ...grid2, marginBottom: 16 }}>
          {/* Usuário */}
          <div>
            <label style={labelStyle}>Usuário (E-mail)</label>
            <input value={form.usuario_email} onChange={e => setForm(p => ({ ...p, usuario_email: e.target.value }))}
              style={inputStyle} placeholder="login@email.com" />
          </div>
          {/* Senha */}
          <div>
            <label style={labelStyle}>
              Senha {form.senha && <span style={{ color: forcaCor, fontWeight: 600, fontSize: 12 }}>● {forcaLabel}</span>}
            </label>
            <div style={{ position: 'relative' }}>
              <input type={mostrarSenha ? 'text' : 'password'} value={form.senha}
                onChange={e => setForm(p => ({ ...p, senha: e.target.value }))}
                style={{ ...inputStyle, paddingRight: 40 }}
                placeholder={carregandoSenha ? 'Carregando...' : (senha ? '(manter atual)' : 'Digite ou gere abaixo')}
                disabled={carregandoSenha} />
              <button onClick={() => setMostrarSenha(p => !p)} disabled={carregandoSenha}
                style={{ position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', fontSize: 16, opacity: carregandoSenha ? 0.4 : 1 }}>
                {mostrarSenha ? '🙈' : '👁'}
              </button>
            </div>
            {form.senha && (
              <div style={{ height: 4, borderRadius: 2, background: '#eee', marginTop: 4, overflow: 'hidden' }}>
                <div style={{ width: `${forca * 20}%`, height: '100%', background: forcaCor, transition: 'all .3s' }} />
              </div>
            )}
          </div>
        </div>

        {/* Gerador */}
        <div style={{ background: '#f5f7fa', border: '1px solid #d4dde8', borderRadius: 8, padding: 14, marginBottom: 16 }}>
          <p style={{ margin: '0 0 10px', fontWeight: 600, color: '#1A2233', fontSize: 13 }}>🎲 Gerador de senhas</p>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, alignItems: 'center' }}>
            {[['maiuscula','Maiúscula'],['minuscula','Minúscula'],['numeros','Números'],['simbolos','Símbolos']].map(([k,l]) => (
              <label key={k} style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer', fontSize: 13 }}>
                <input type="checkbox" checked={geradorOpts[k]} onChange={e => setGeradorOpts(p => ({ ...p, [k]: e.target.checked }))} /> {l}
              </label>
            ))}
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginLeft: 'auto' }}>
              <input type="number" value={geradorOpts.tamanho} min={6} max={64}
                onChange={e => setGeradorOpts(p => ({ ...p, tamanho: parseInt(e.target.value) || 12 }))}
                style={{ ...inputStyle, width: 60, padding: '6px 10px' }} />
              <button onClick={gerarSenha} style={{ ...btnPrimary, padding: '8px 16px', fontSize: 13 }}>Gerar senha</button>
            </div>
          </div>
        </div>

        {/* Link */}
        <div style={{ marginBottom: 16 }}>
          <label style={labelStyle}>Link</label>
          <div style={{ display: 'flex', gap: 8 }}>
            <input value={form.link} onChange={e => setForm(p => ({ ...p, link: e.target.value }))}
              style={{ ...inputStyle, flex: 1 }} placeholder="https://..." />
            {form.link && (
              <a href={form.link} target="_blank" rel="noopener noreferrer"
                style={{ ...btnPrimary, padding: '10px 14px', textDecoration: 'none', display: 'flex', alignItems: 'center' }}>→</a>
            )}
          </div>
        </div>

        {/* Observação */}
        <div style={{ marginBottom: 16 }}>
          <label style={labelStyle}>Observação</label>
          <textarea value={form.observacao} onChange={e => setForm(p => ({ ...p, observacao: e.target.value }))}
            rows={3} style={{ ...inputStyle, resize: 'vertical', fontFamily: 'inherit' }} placeholder="Informações adicionais..." />
        </div>

        {/* Visibilidade */}
        <div style={{ background: '#fff3cd', border: '1px solid #ffc107', borderRadius: 8, padding: '10px 14px', marginBottom: 14 }}>
          <p style={{ margin: 0, fontWeight: 600, color: '#856404', fontSize: 13 }}>
            ⚠️ Os usuários administradores têm permissão de ver todas as senhas na plataforma.
          </p>
        </div>
        <div style={{ marginBottom: 16 }}>
          <label style={labelStyle}>Perfil de Usuários *</label>
          <div style={{ display: 'flex', gap: 24, marginBottom: 12, flexWrap: 'wrap' }}>
            {[['privado','🔒 Privado'],['customizado','👥 Customizado (Mesas)'],['todos','🌐 Todos podem ver']].map(([v,l]) => (
              <label key={v} style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontSize: 14 }}>
                <input type="radio" name="visibilidade" value={v} checked={form.visibilidade === v}
                  onChange={() => setForm(p => ({ ...p, visibilidade: v, mesa_ids: [] }))} /> {l}
              </label>
            ))}
          </div>
          {form.visibilidade === 'customizado' && (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
              {mesas.map(m => (
                <label key={m.id} style={{
                  display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer',
                  background: form.mesa_ids.includes(m.id) ? '#1A2233' : '#f5f7fa',
                  color: form.mesa_ids.includes(m.id) ? '#fff' : '#1A2233',
                  padding: '6px 14px', borderRadius: 20, fontSize: 13,
                  border: '1px solid #d4dde8', transition: 'all .2s',
                }}>
                  <input type="checkbox" style={{ display: 'none' }} checked={form.mesa_ids.includes(m.id)}
                    onChange={() => setForm(p => ({
                      ...p, mesa_ids: p.mesa_ids.includes(m.id) ? p.mesa_ids.filter(id => id !== m.id) : [...p.mesa_ids, m.id]
                    }))} />
                  {m.nome}
                </label>
              ))}
              {mesas.length === 0 && <p style={{ color: '#888', fontSize: 13 }}>Nenhuma mesa de trabalho cadastrada.</p>}
            </div>
          )}
        </div>

        {/* Notificação */}
        <div style={{ background: '#f8f9fb', border: '1px solid #d4dde8', borderRadius: 10, padding: 16, marginBottom: 16 }}>
          <label style={{ ...labelStyle, marginBottom: 12 }}>🔔 Notificação</label>
          <div style={{ display: 'flex', gap: 28, marginBottom: 14, flexWrap: 'wrap' }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontSize: 14 }}>
              <input type="radio" name="notif" checked={form.notificacao_tipo === 'nunca'}
                onChange={() => setForm(p => ({ ...p, notificacao_tipo: 'nunca', expira_em_dias: '', notificacao_acao: 'nenhuma' }))} />
              Nunca expira
            </label>
            <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontSize: 14 }}>
              <input type="radio" name="notif" checked={form.notificacao_tipo === 'expira'}
                onChange={() => setForm(p => ({ ...p, notificacao_tipo: 'expira' }))} />
              Essa senha expira em
              <input type="number" value={form.expira_em_dias} min={1}
                disabled={form.notificacao_tipo !== 'expira'}
                onChange={e => setForm(p => ({ ...p, expira_em_dias: e.target.value }))}
                style={{ ...inputStyle, width: 70, padding: '5px 10px', display: 'inline-block',
                  opacity: form.notificacao_tipo !== 'expira' ? 0.4 : 1 }} />
              dias
            </label>
          </div>

          {form.notificacao_tipo === 'expira' && (
            <div style={{ ...grid2, alignItems: 'end', gap: 12 }}>
              <div>
                <label style={labelStyle}>Ação</label>
                <select value={form.notificacao_acao}
                  onChange={e => setForm(p => ({ ...p, notificacao_acao: e.target.value }))}
                  style={inputStyle}>
                  <option value="nenhuma">Nenhuma</option>
                  <option value="email">Enviar email</option>
                  <option value="alerta">Enviar alerta</option>
                  <option value="ticket">Criar ticket</option>
                </select>
              </div>
              {form.notificacao_acao !== 'nenhuma' && (
                <div>
                  <label style={labelStyle}>Com antecedência de</label>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <input type="number" value={form.notificacao_antecedencia} min={1}
                      onChange={e => setForm(p => ({ ...p, notificacao_antecedencia: e.target.value }))}
                      style={{ ...inputStyle, width: 80, padding: '6px 10px' }} />
                    <span style={{ fontSize: 14, color: '#555', whiteSpace: 'nowrap' }}>dia(s) antes que expire</span>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Datas (só na edição) */}
        {senha && (
          <div style={{ ...grid2, marginBottom: 16 }}>
            <div>
              <label style={labelStyle}>Data de criação</label>
              <input readOnly value={senha.criado_em ? new Date(senha.criado_em).toLocaleString('pt-BR') : '—'}
                style={{ ...inputStyle, background: '#f5f7fa', color: '#888', cursor: 'default' }} />
            </div>
            <div>
              <label style={labelStyle}>Data de alteração</label>
              <input readOnly value={senha.atualizado_em ? new Date(senha.atualizado_em).toLocaleString('pt-BR') : '—'}
                style={{ ...inputStyle, background: '#f5f7fa', color: '#888', cursor: 'default' }} />
            </div>
          </div>
        )}

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 12, marginTop: 8 }}>
          <button onClick={onFechar} style={btnSecondary}>Cancelar</button>
          <button onClick={salvar} disabled={loading} style={btnPrimary}>
            {loading ? 'Salvando...' : '💾 Salvar'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Modal Revelar ────────────────────────────────────────────────────────────
function ModalRevelar({ senhaItem, onFechar, toast }) {
  const [senhaTexto, setSenhaTexto] = useState('');
  const [copiado, setCopiado] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get(`/cofre/senhas/${senhaItem.id}/revelar`)
      .then(({ data }) => setSenhaTexto(data.senha))
      .catch(e => { toast(e.response?.data?.erro || 'Erro ao revelar.', 'erro'); onFechar(); })
      .finally(() => setLoading(false));
  }, [senhaItem.id]);

  async function copiar() {
    try { await api.post(`/cofre/senhas/${senhaItem.id}/copiar`); } catch {}
    navigator.clipboard.writeText(senhaTexto);
    setCopiado(true);
    setTimeout(() => setCopiado(false), 2000);
  }

  return (
    <div style={overlayStyle}>
      <div style={{ ...modalStyle, maxWidth: 420 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <h3 style={{ color: '#1A2233', margin: 0 }}>👁 Revelar Senha</h3>
          <button onClick={onFechar} style={btnFechar}>×</button>
        </div>
        <p style={{ color: '#555', marginBottom: 4, fontSize: 13 }}><strong>{senhaItem.cliente_nome}</strong> — {senhaItem.descricao}</p>
        <p style={{ color: '#888', fontSize: 12, marginBottom: 16 }}>⚠️ Esta ação foi registrada no log de auditoria.</p>
        {loading ? (
          <div style={{ textAlign: 'center', padding: 24, color: '#888' }}>Descriptografando...</div>
        ) : (
          <div style={{ background: '#1A2233', color: '#4ade80', fontFamily: 'monospace', fontSize: 18,
            padding: '16px 20px', borderRadius: 8, letterSpacing: 2, wordBreak: 'break-all', marginBottom: 16,
            display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
            <span>{senhaTexto}</span>
            <button onClick={copiar} style={{ background: copiado ? '#16a085' : '#D85A30', border: 'none',
              borderRadius: 6, padding: '8px 14px', color: '#fff', cursor: 'pointer', fontSize: 13, whiteSpace: 'nowrap' }}>
              {copiado ? '✅ Copiado!' : '📋 Copiar'}
            </button>
          </div>
        )}
        <button onClick={onFechar} style={{ ...btnSecondary, width: '100%' }}>Fechar</button>
      </div>
    </div>
  );
}

// ─── Modal Exportar ───────────────────────────────────────────────────────────
function ModalExportar({ clientes, onFechar, toast }) {
  const [clienteFiltro, setClienteFiltro] = useState('');
  const [senhaMaster, setSenhaMaster] = useState('');
  const [loading, setLoading] = useState(false);
  const [erro, setErro] = useState('');

  async function exportar() {
    if (!senhaMaster) { setErro('Informe sua senha master.'); return; }
    setLoading(true); setErro('');
    try {
      const token = localStorage.getItem('token');
      const baseUrl = (process.env.REACT_APP_API_URL || 'https://reembolso-backend-x7k8.onrender.com').replace(/\/api$/, '');
      const response = await fetch(`${baseUrl}/api/cofre/exportar`, { method: 'POST', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
          body: JSON.stringify({ senha_master: senhaMaster, cliente_nome: clienteFiltro || undefined }) }
      );
      if (!response.ok) { const d = await response.json(); setErro(d.erro || 'Erro ao exportar.'); setLoading(false); return; }
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `cofre_${clienteFiltro || 'todos'}_${new Date().toISOString().slice(0,10)}.csv`;
      a.click();
      URL.revokeObjectURL(url);
      toast('✅ Exportação realizada!', 'ok');
      onFechar();
    } catch { setErro('Erro ao exportar.'); }
    setLoading(false);
  }

  return (
    <div style={overlayStyle}>
      <div style={{ ...modalStyle, maxWidth: 440 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
          <h2 style={{ color: '#1A2233', margin: 0 }}>📤 Exportar Senhas</h2>
          <button onClick={onFechar} style={btnFechar}>×</button>
        </div>
        <div style={{ background: '#fef3cd', border: '1px solid #ffc107', borderRadius: 8, padding: 12, marginBottom: 16, fontSize: 13, color: '#856404' }}>
          ⚠️ <strong>Ação sensível:</strong> exportação com senhas visíveis, registrada no log de auditoria.
        </div>
        <label style={labelStyle}>Filtrar por cliente (opcional)</label>
        <select value={clienteFiltro} onChange={e => setClienteFiltro(e.target.value)} style={{ ...inputStyle, marginBottom: 14 }}>
          <option value="">Todos os clientes</option>
          {clientes.map(c => <option key={c} value={c}>{c}</option>)}
        </select>
        <label style={labelStyle}>🔑 Sua senha master *</label>
        <input type="password" value={senhaMaster} onChange={e => setSenhaMaster(e.target.value)}
          style={inputStyle} placeholder="Digite sua senha master" />
        {erro && <p style={erroStyle}>{erro}</p>}
        <div style={{ display: 'flex', gap: 12, marginTop: 20 }}>
          <button onClick={onFechar} style={{ ...btnSecondary, flex: 1 }}>Cancelar</button>
          <button onClick={exportar} disabled={loading} style={{ ...btnPrimary, flex: 1 }}>
            {loading ? 'Exportando...' : '📥 Baixar CSV'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Componente Principal ─────────────────────────────────────────────────────
export default function Cofre({ onVoltar }) {
  const usuario = JSON.parse(localStorage.getItem('usuario') || '{}');
  const isGestor = usuario.perfil === 'ges' || (usuario.perfis || []).includes('ges');
  const { msgs, toast, remove } = useToast();

  const [mfaOk, setMfaOk]           = useState(false);
  const [checkingMfa, setCheckingMfa] = useState(true);
  const [mfaCancelado, setMfaCancelado] = useState(false);
  const [senhas, setSenhas]           = useState([]);
  const [total, setTotal]             = useState(0);
  const [loading, setLoading]         = useState(false);
  const [pagina, setPagina]           = useState(1);
  const limite = 12;

  const [filtros, setFiltros]         = useState({ cliente: '', busca: '' });
  const [clientes, setClientes]       = useState([]);
  const [mesas, setMesas]             = useState([]);

  const [modalNova, setModalNova]     = useState(false);
  const [modalEditar, setModalEditar] = useState(null);
  const [modalRevelar, setModalRevelar] = useState(null);
  const [modalExportar, setModalExportar] = useState(false);

  useEffect(() => {
    api.get('/cofre/mfa/status')
      .then(({ data }) => setMfaOk(data.autenticado))
      .catch(() => {})
      .finally(() => setCheckingMfa(false));
  }, []);

  useEffect(() => { if (mfaOk) { carregarSenhas(); carregarMetas(); } }, [mfaOk, pagina, filtros]);

  async function carregarSenhas() {
    setLoading(true);
    try {
      const params = new URLSearchParams({ pagina, limite,
        ...(filtros.cliente && { cliente: filtros.cliente }),
        ...(filtros.busca   && { busca:   filtros.busca }),
      });
      const { data } = await api.get(`/cofre/senhas?${params}`);
      setSenhas(data.senhas);
      setTotal(data.total);
    } catch {}
    setLoading(false);
  }

  async function carregarMetas() {
    try {
      const [rc, rm] = await Promise.all([api.get('/cofre/clientes'), api.get('/cofre/mesas')]);
      setClientes(rc.data);
      setMesas(rm.data);
    } catch {}
  }

  async function salvarSenha(form) {
    const isEdit = !!modalEditar;
    await api[isEdit ? 'put' : 'post'](isEdit ? `/cofre/senhas/${modalEditar.id}` : '/cofre/senhas', form);
    toast(isEdit ? '✅ Senha atualizada!' : '✅ Senha cadastrada!', 'ok');
    setModalNova(false); setModalEditar(null);
    carregarSenhas(); carregarMetas();
  }

  async function deletar(s) {
    if (!window.confirm(`Excluir "${s.descricao}"?`)) return;
    try { await api.delete(`/cofre/senhas/${s.id}`); toast('🗑️ Removida.', 'ok'); carregarSenhas(); }
    catch (e) { toast(e.response?.data?.erro || 'Erro ao excluir.', 'erro'); }
  }

  function aplicarFiltros(e) { e.preventDefault(); setPagina(1); carregarSenhas(); }
  const totalPaginas = Math.ceil(total / limite);

  if (checkingMfa) return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: 300 }}>
      <div style={{ textAlign: 'center', color: '#888' }}><div style={{ fontSize: 40 }}>🔐</div><p>Verificando autenticação...</p></div>
    </div>
  );
  if (mfaCancelado) return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: 300 }}>
      <div style={{ textAlign: 'center', color: '#888' }}>
        <div style={{ fontSize: 40 }}>🔒</div>
        <p style={{ marginBottom: 16 }}>Acesso ao cofre cancelado.</p>
        <button onClick={() => setMfaCancelado(false)} style={{ background: '#D85A30', color: '#fff', border: 'none', borderRadius: 8, padding: '9px 20px', fontWeight: 700, fontSize: 13, cursor: 'pointer' }}>
          Tentar novamente
        </button>
      </div>
    </div>
  );
  if (!mfaOk) return <MfaModal onAutenticado={() => setMfaOk(true)} onCancelar={() => { if (onVoltar) onVoltar(); else setMfaCancelado(true); }} />;

  return (
    <div style={{ padding: 24, background: '#f5f7fa', minHeight: '100vh' }}>
      <Toast msgs={msgs} onRemove={remove} />

      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
        <h1 style={{ margin: 0, color: '#1A2233', fontSize: 22, fontWeight: 700 }}>🔐 Cofre de Senhas</h1>
        <div style={{ display: 'flex', gap: 10 }}>
          {isGestor && <button onClick={() => setModalExportar(true)} style={btnSecondary}>📤 Exportar</button>}
          <button onClick={() => setModalNova(true)} style={btnPrimary}>+ Nova senha</button>
        </div>
      </div>

      {/* Filtros */}
      <div style={{ background: '#fff', borderRadius: 10, border: '1px solid #d4dde8', padding: '12px 16px', marginBottom: 16 }}>
        <form onSubmit={aplicarFiltros} style={{ display: 'flex', flexWrap: 'wrap', gap: 10, alignItems: 'flex-end' }}>
          <div>
            <label style={{ fontSize: 12, color: '#888', display: 'block', marginBottom: 4 }}>Cliente</label>
            <select value={filtros.cliente} onChange={e => setFiltros(p => ({ ...p, cliente: e.target.value }))}
              style={{ ...inputStyle, padding: '6px 12px', minWidth: 140 }}>
              <option value="">Todos</option>
              {clientes.map(c => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>
          <div>
            <label style={{ fontSize: 12, color: '#888', display: 'block', marginBottom: 4 }}>Busca</label>
            <input value={filtros.busca} onChange={e => setFiltros(p => ({ ...p, busca: e.target.value }))}
              style={{ ...inputStyle, padding: '6px 12px', minWidth: 220 }} placeholder="Buscar por descrição ou cliente..." />
          </div>
          <button type="submit" style={{ ...btnPrimary, padding: '8px 20px' }}>Filtrar</button>
          <button type="button" onClick={() => { setFiltros({ cliente: '', busca: '' }); setPagina(1); }}
            style={{ ...btnSecondary, padding: '8px 16px' }}>✕</button>
        </form>
      </div>

      {/* Tabela — duplo clique abre edição */}
      <div style={{ background: '#fff', borderRadius: 10, border: '1px solid #d4dde8', overflow: 'hidden' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ background: '#f8f9fb', borderBottom: '2px solid #d4dde8' }}>
              {['Cliente','Descrição','Usuário','Senha','Link','Observação','Notificação','Criado em','Alterado em',''].map(h => (
                <th key={h} style={{ padding: '12px 14px', textAlign: 'left', fontSize: 12, fontWeight: 700, color: '#1A2233', whiteSpace: 'nowrap', textTransform: 'uppercase' }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={10} style={{ textAlign: 'center', padding: 40, color: '#888' }}>Carregando...</td></tr>
            ) : senhas.length === 0 ? (
              <tr><td colSpan={10} style={{ textAlign: 'center', padding: 40, color: '#888' }}>
                <div style={{ fontSize: 32, marginBottom: 8 }}>🔒</div>Nenhuma senha encontrada.
              </td></tr>
            ) : senhas.map(s => (
              <tr key={s.id}
                onDoubleClick={() => setModalEditar(s)}
                title="Clique duplo para editar"
                style={{ borderBottom: '1px solid #f0f0f0', cursor: 'pointer' }}
                onMouseEnter={e => e.currentTarget.style.background = '#fafafa'}
                onMouseLeave={e => e.currentTarget.style.background = ''}>
                <td style={{ ...tdStyle, maxWidth: 140 }} title={s.cliente_nome || ''}>
                  <span style={{ display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {s.cliente_nome || '---'}
                  </span>
                </td>
                <td style={{ ...tdStyle, maxWidth: 220 }} title={s.descricao || ''}>
                  <span style={{ display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', cursor: 'help' }}>
                    {s.descricao || '---'}
                  </span>
                </td>
                <td style={{ ...tdStyle, maxWidth: 140 }} title={s.usuario_email || ''}>
                  <span style={{ display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {s.usuario_email || '---'}
                  </span>
                </td>
                <td style={tdStyle}><span style={{ fontFamily: 'monospace', letterSpacing: 2 }}>••••••••••••</span></td>
                <td style={tdStyle}>
                  {s.link ? <a href={s.link} target="_blank" rel="noopener noreferrer" style={{ color: '#D85A30' }}
                    onClick={e => e.stopPropagation()}>🔗</a> : '---'}
                </td>
                <td style={{ ...tdStyle, maxWidth: 180 }} title={s.observacao || ''}>
                  <span style={{ display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', cursor: 'help' }}>
                    {s.observacao || '---'}
                  </span>
                </td>
                <td style={tdStyle}>
                  {s.expira_em_dias
                    ? <span style={{ background: '#fff3e0', color: '#D85A30', padding: '2px 8px', borderRadius: 10, fontSize: 11, fontWeight: 600 }}>
                        ⏰ {s.expira_em_dias}d
                      </span>
                    : <span style={{ color: '#888', fontSize: 12 }}>—</span>}
                </td>
                <td style={{ ...tdStyle, fontSize: 11, color: '#888', whiteSpace: 'nowrap' }}>
                  {s.criado_em ? new Date(s.criado_em).toLocaleDateString('pt-BR') : '—'}
                </td>
                <td style={{ ...tdStyle, fontSize: 11, color: '#888', whiteSpace: 'nowrap' }}>
                  {s.atualizado_em ? new Date(s.atualizado_em).toLocaleDateString('pt-BR') : '—'}
                </td>
                <td style={{ padding: '8px 10px', whiteSpace: 'nowrap' }} onClick={e => e.stopPropagation()}>
                  <button onClick={() => setModalRevelar(s)} title="Ver senha" style={btnIcone('#f5f7fa','#1A2233')}>👁</button>
                  <button onClick={() => setModalEditar(s)} title="Editar" style={btnIcone('#e8f5e9','#27ae60')}>✏️</button>
                  {(isGestor || s.criado_por === usuario.id) && (
                    <button onClick={() => deletar(s)} title="Excluir" style={btnIcone('#fdecea','#c0392b')}>🗑</button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        {totalPaginas > 1 && (
          <div style={{ padding: '12px 16px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderTop: '1px solid #f0f0f0' }}>
            <span style={{ fontSize: 13, color: '#888' }}>
              Mostrando {(pagina-1)*limite+1} a {Math.min(pagina*limite,total)} de {total} itens.
            </span>
            <div style={{ display: 'flex', gap: 6 }}>
              <button onClick={() => setPagina(1)} disabled={pagina===1} style={btnPag}>«</button>
              <button onClick={() => setPagina(p => p-1)} disabled={pagina===1} style={btnPag}>‹</button>
              {Array.from({ length: Math.min(5, totalPaginas) }, (_, i) => {
                const p = Math.max(1, Math.min(pagina-2, totalPaginas-4)) + i;
                return <button key={p} onClick={() => setPagina(p)}
                  style={{ ...btnPag, background: p===pagina ? '#1A2233' : '#fff', color: p===pagina ? '#fff' : '#1A2233' }}>{p}</button>;
              })}
              <button onClick={() => setPagina(p => p+1)} disabled={pagina===totalPaginas} style={btnPag}>›</button>
              <button onClick={() => setPagina(totalPaginas)} disabled={pagina===totalPaginas} style={btnPag}>»</button>
            </div>
          </div>
        )}
      </div>

      {/* Modais */}
      {(modalNova || modalEditar) && (
        <ModalSenha senha={modalEditar} mesas={mesas} onSalvar={salvarSenha}
          onFechar={() => { setModalNova(false); setModalEditar(null); }} />
      )}
      {modalRevelar && <ModalRevelar senhaItem={modalRevelar} onFechar={() => setModalRevelar(null)} toast={toast} />}
      {modalExportar && <ModalExportar clientes={clientes} onFechar={() => setModalExportar(false)} toast={toast} />}
    </div>
  );
}

// ─── Helpers & Estilos ────────────────────────────────────────────────────────
function truncar(str, max) { if (!str) return ''; return str.length > max ? str.slice(0,max)+'...' : str; }

const overlayStyle = { position:'fixed', inset:0, background:'rgba(26,34,51,0.55)', display:'flex', alignItems:'center', justifyContent:'center', zIndex:1000, padding:16 };
const modalStyle   = { background:'#fff', borderRadius:12, padding:28, width:'100%', boxShadow:'0 8px 40px rgba(0,0,0,0.18)' };
const inputStyle   = { width:'100%', padding:'10px 14px', border:'1px solid #d4dde8', borderRadius:7, fontSize:14, boxSizing:'border-box', outline:'none', background:'#fff', color:'#1A2233' };
const labelStyle   = { fontSize:13, fontWeight:600, color:'#1A2233', display:'block', marginBottom:6 };
const btnPrimary   = { background:'#D85A30', color:'#fff', border:'none', borderRadius:7, padding:'10px 22px', fontSize:14, fontWeight:600, cursor:'pointer' };
const btnSecondary = { background:'#fff', color:'#1A2233', border:'1px solid #d4dde8', borderRadius:7, padding:'10px 22px', fontSize:14, fontWeight:600, cursor:'pointer' };
const btnFechar    = { background:'none', border:'none', fontSize:22, cursor:'pointer', color:'#888', lineHeight:1 };
const erroStyle    = { color:'#c0392b', fontSize:13, background:'#fdecea', padding:'8px 12px', borderRadius:6, margin:'8px 0' };
const tdStyle      = { padding:'12px 14px', fontSize:13, color:'#1A2233', verticalAlign:'middle' };
const grid2        = { display:'grid', gridTemplateColumns:'1fr 1fr', gap:16 };
const btnPag       = { padding:'6px 12px', border:'1px solid #d4dde8', borderRadius:6, background:'#fff', cursor:'pointer', fontSize:13, color:'#1A2233' };
function btnIcone(bg, color) { return { background:bg, color, border:'none', borderRadius:6, padding:'6px 10px', cursor:'pointer', fontSize:14, marginRight:4 }; }
